import moment from "moment";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {
  texto,
  moneda,
  fecha,
  centro,
  construirHojaTabla,
  limpiarNombreArchivo,
  paraPdf,
  descargarLibro,
  totalizar,
} from "../../../../Components/ExcelTablaReporte";
import reporteFiscalPdf from "../../../../Pdf/reporteFiscalPdf";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/**
 * Excel y PDF de las dos hojas de trabajo del Reporte de IVA.
 *
 * Cada hoja se arma una sola vez como `{ columnas, filas, totales }` y de ahi
 * salen los dos archivos, para que no puedan decir cosas distintas (mismo patron
 * que `ReporteCostoVsUltimaCompra/exportarCostoVsUltimaCompra.js`).
 *
 * Las notas de credito siguen el criterio de la DIOT y de la contabilidad:
 *  - la NOTA llega con todos sus importes en negativo (`tipo === "NOTA_CREDITO"`);
 *  - su APLICACION a un saldo llega en positivo como un pago mas del documento
 *    (`tipo === "APLICACION_NC"`).
 * Una nota aplicada a un saldo pendiente se anula con su aplicacion; aqui no se
 * cambia ningun signo, solo se etiquetan.
 */

export const esNota = (registro) => registro && registro.tipo === "NOTA_CREDITO";

export const esAplicacionNota = (registro) => registro && registro.tipo === "APLICACION_NC";

// Centavos de redondeo: la nota calcula su IVA renglon por renglon y la aplicacion
// lo prorratea con los totales del documento.
const TOLERANCIA_ANULACION = 0.1;

/**
 * Los renglones de las notas que se anulan solas: la NOTA mas sus aplicaciones del
 * periodo (se juntan por `notaId`) cuando, sumadas, no mueven ninguno de los
 * `campos` fiscales (bases, IVA, retenciones). Es la nota aplicada completa a un
 * saldo pendiente.
 *
 * El Monto no se compara: en moneda extranjera la aplicacion lo trae en la moneda
 * del pago y la nota en la de contabilidad.
 *
 * Una nota que si mueve el IVA del periodo no se anula y se sigue viendo: la que se
 * reembolso o quedo como saldo a favor, la aplicada a medias o en otro periodo, o la
 * de tasas distintas a las del documento.
 */
export const filasQueSeAnulan = (registros, campos) => {
  const grupos = new Map();
  for (const registro of registros || []) {
    if (!registro.notaId || !(esNota(registro) || esAplicacionNota(registro))) continue;
    const llave = String(registro.notaId);
    if (!grupos.has(llave)) grupos.set(llave, { nota: null, aplicaciones: [] });
    const grupo = grupos.get(llave);
    if (esNota(registro)) grupo.nota = registro;
    else grupo.aplicaciones.push(registro);
  }

  const anuladas = new Set();
  for (const { nota, aplicaciones } of grupos.values()) {
    if (!nota || aplicaciones.length === 0) continue;
    const filas = [nota, ...aplicaciones];
    const seAnulan = campos.every(
      (campo) =>
        Math.abs(filas.reduce((acc, fila) => acc + (Number(fila[campo]) || 0), 0)) <
        TOLERANCIA_ANULACION
    );
    if (seAnulan) filas.forEach((fila) => anuladas.add(fila));
  }
  return anuladas;
};

const etiquetaTipo = (registro) => {
  if (esNota(registro)) return "NC";
  if (esAplicacionNota(registro)) return "Aplic. NC";
  return "";
};

// El mismo rosa que usa el resalte "nota" del Excel, para que las dos salidas se
// vean igual. `reporteFiscalPdf` respeta `fillColor` por celda.
const FONDO_NOTA_PDF = "#FDE7E9";

/**
 * Marca todo el renglon de una nota de credito. Cada renderer entiende una llave
 * distinta: el Excel el `resalte` (ver RESALTES en ExcelTablaReporte) y el PDF el
 * `fillColor`; se ponen las dos y cada uno ignora la que no le toca.
 */
const resaltarNota = (fila) =>
  fila.map((celda) => ({ ...celda, resalte: "nota", fillColor: FONDO_NOTA_PDF }));

const filaSegunTipo = (registro, celdas) => (esNota(registro) ? resaltarNota(celdas) : celdas);

// El PDF va en LETTER horizontal y no le caben todas las columnas: se queda con la
// identificacion del renglon, el 16% (la tasa con la que se mueve casi todo) y
// las retenciones. El desglose completo por tasa vive en el Excel.
const COLUMNAS_COBROS = [
  // Ancho de sobra para la palabra TOTAL, que `construirHojaTabla` y
  // `reporteFiscalPdf` ponen siempre en la primera columna.
  { titulo: "Tipo", tipo: "centro", ancho: 10, anchoPdf: 40 },
  { titulo: "Fecha", tipo: "fecha", ancho: 12, anchoPdf: 48 },
  { titulo: "Cliente", tipo: "texto", ancho: 34, anchoPdf: 124 },
  { titulo: "Documento", tipo: "texto", ancho: 14, anchoPdf: 60 },
  // En una nota: las ventas que relaciona. En una aplicacion: la nota aplicada.
  { titulo: "Relacionado", tipo: "texto", ancho: 16, anchoPdf: 60 },
  { titulo: "Monto", tipo: "moneda", ancho: 15, anchoPdf: 62 },
  { titulo: "Total a Pagar", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "Base 16%", tipo: "moneda", ancho: 15, anchoPdf: 62 },
  { titulo: "IVA 16%", tipo: "moneda", ancho: 15, anchoPdf: 62 },
  { titulo: "Base 8%", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "IVA 8%", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "Base 11%", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "IVA 11%", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "Base 0%", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "Exentos", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "Otras Tasas", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "Base 15%", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "IVA 15%", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "Base 10%", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "IVA 10%", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "IVA Retenido", tipo: "moneda", ancho: 15, anchoPdf: 62 },
  { titulo: "ISR Retenido", tipo: "moneda", ancho: 15, anchoPdf: 62 },
  { titulo: "Otras Retenciones", tipo: "moneda", ancho: 16, enPdf: false },
  { titulo: "Total IVA", tipo: "moneda", ancho: 16, anchoPdf: 66 },
];

// En pagos van tres columnas mas al PDF (Relacionado, IEPS y el Tipo); los anchos
// son mas justos para que las 12 quepan en la hoja con su relleno de 3 pt por lado.
const COLUMNAS_PAGOS = [
  { titulo: "Tipo", tipo: "centro", ancho: 10, anchoPdf: 36 },
  { titulo: "Fecha", tipo: "fecha", ancho: 12, anchoPdf: 44 },
  { titulo: "Proveedor", tipo: "texto", ancho: 34, anchoPdf: 100 },
  { titulo: "Folio", tipo: "texto", ancho: 16, anchoPdf: 52 },
  // En una nota: las facturas que relaciona. En una aplicacion: la nota aplicada.
  { titulo: "Relacionado", tipo: "texto", ancho: 16, anchoPdf: 52 },
  { titulo: "Descripcion", tipo: "texto", ancho: 30, enPdf: false },
  { titulo: "UUID", tipo: "texto", ancho: 38, enPdf: false },
  { titulo: "Monto", tipo: "moneda", ancho: 15, anchoPdf: 56 },
  { titulo: "Total a Pagar", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "Base 16%", tipo: "moneda", ancho: 15, anchoPdf: 56 },
  { titulo: "IVA 16%", tipo: "moneda", ancho: 15, anchoPdf: 54 },
  { titulo: "Base 8%", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "IVA 8%", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "Base 11%", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "IVA 11%", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "Base 0%", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "Exentos", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "Base 15%", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "IVA 15%", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "Base 10%", tipo: "moneda", ancho: 15, enPdf: false },
  { titulo: "IVA 10%", tipo: "moneda", ancho: 15, enPdf: false },
  // El IEPS no es IVA acreditable: se informa aparte, sin entrar al Total IVA.
  { titulo: "IEPS", tipo: "moneda", ancho: 15, anchoPdf: 50 },
  { titulo: "IVA Retenido", tipo: "moneda", ancho: 15, anchoPdf: 50 },
  { titulo: "ISR Retenido", tipo: "moneda", ancho: 15, anchoPdf: 50 },
  { titulo: "Otras Retenciones", tipo: "moneda", ancho: 16, enPdf: false },
  { titulo: "Total IVA", tipo: "moneda", ancho: 16, anchoPdf: 58 },
];

/**
 * "Total a Pagar" es el total del DOCUMENTO, y se repite en cada aplicacion de
 * pago: sumarlo no significa nada (una factura liquidada en tres exhibiciones lo
 * contaria tres veces). Se deja sin total, igual que en la tabla de pantalla.
 */
const totalesSinTotalPagar = (columnas, filas) => {
  const totales = totalizar(columnas, filas);
  const indice = columnas.findIndex((columna) => columna.titulo === "Total a Pagar");
  if (indice >= 0) totales[indice] = null;
  return totales;
};

const construirTablaCobros = (registros) => {
  const filas = (registros || []).map((registro) => filaSegunTipo(registro, [
    centro(etiquetaTipo(registro)),
    fecha(registro.fechaPago),
    texto(registro.nombreCliente),
    texto(registro.serieNumero),
    texto(registro.relacionado),
    moneda(registro.monto),
    moneda(registro.totalPagar),
    moneda(registro.actosGravados16),
    moneda(registro.ivaCausado16),
    moneda(registro.actosGravados8),
    moneda(registro.ivaCausado8),
    moneda(registro.actosGravados11),
    moneda(registro.ivaCausado11),
    moneda(registro.actosGravados0),
    moneda(registro.actosExentos),
    moneda(registro.actosOtrasTasas),
    moneda(registro.actosGravados15),
    moneda(registro.ivaCausado15),
    moneda(registro.actosGravados10),
    moneda(registro.ivaCausado10),
    moneda(registro.ivaRetenido),
    moneda(registro.isrRetenido),
    moneda(registro.otrasRetenciones),
    moneda(registro.totalIvaFila),
  ]));

  return {
    columnas: COLUMNAS_COBROS,
    filas,
    totales: totalesSinTotalPagar(COLUMNAS_COBROS, filas),
  };
};

const construirTablaPagos = (registros) => {
  const filas = (registros || []).map((registro) => filaSegunTipo(registro, [
    centro(etiquetaTipo(registro)),
    fecha(registro.fechaPago),
    texto(registro.proveedorNombre),
    texto(registro.numeroFactura),
    texto(registro.relacionado),
    texto(registro.descripcionFactura),
    texto(registro.uuid),
    moneda(registro.monto),
    moneda(registro.totalPagar),
    moneda(registro.actosPagados16),
    moneda(registro.ivaActosPagados16),
    moneda(registro.actosPagados8),
    moneda(registro.ivaActosPagados8),
    moneda(registro.actosPagados11),
    moneda(registro.ivaActosPagados11),
    moneda(registro.actosPagados0),
    moneda(registro.actosExentosPagados),
    moneda(registro.actosPagados15),
    moneda(registro.ivaActosPagados15),
    moneda(registro.actosPagados10),
    moneda(registro.ivaActosPagados10),
    moneda(registro.iepsPagado),
    moneda(registro.ivaRetenido),
    moneda(registro.isrRetenido),
    moneda(registro.otrasRetenciones),
    moneda(registro.totalIvaFila),
  ]));

  return {
    columnas: COLUMNAS_PAGOS,
    filas,
    totales: totalesSinTotalPagar(COLUMNAS_PAGOS, filas),
  };
};

const TITULO_COBROS = "Papel de trabajo - Cobros (IVA Causado)";
const TITULO_PAGOS = "Papel de trabajo - Pagos a Proveedores (IVA Acreditable)";

const contarNotas = (tabla) => tabla.filas.filter((fila) => fila[0].valor === "NC").length;

/**
 * El periodo consultado, la base de calculo y -cuando hay- cuantas notas de
 * credito trae la hoja y como se leen, para que quien la abra no tenga que
 * descubrir a mano por que hay renglones en negativo. Tambien cuantas se
 * ocultaron por anularse con su aplicacion, para que no parezca que faltan.
 */
const descripcionFiltros = (periodo, tabla, notasOcultas = 0) => {
  const rango = periodo && periodo[0] && periodo[1]
    ? `Periodo ${moment(periodo[0]).format("DD/MM/YYYY")} - ${moment(periodo[1]).format("DD/MM/YYYY")}`
    : "Periodo no especificado";
  const notas = contarNotas(tabla);
  const partes = [rango, "Base: flujo (fecha de pago)"];
  if (notas > 0) {
    partes.push(`${notas} nota(s) de credito, marcadas NC y en negativo (restan); su aplicacion a un saldo va como "Aplic. NC" y la compensa`);
  }
  if (notasOcultas > 0) {
    partes.push(`${notasOcultas} nota(s) de credito no se muestran junto con su aplicacion: se anulan entre si y no mueven el IVA`);
  }
  if (notas === 0 && notasOcultas === 0) partes.push("Sin notas de credito en el periodo");
  return partes.join("  |  ");
};

const nombreArchivo = (prefijo, empresaNombre, periodo) =>
  `${prefijo}_${limpiarNombreArchivo(empresaNombre)}_${
    periodo && periodo[0] ? moment(periodo[0]).format("YYYY-MM") : moment().format("YYYY-MM")
  }`;

const indicadoresDe = (tabla) => {
  const totalIva = tabla.totales[tabla.columnas.length - 1];
  return [
    { etiqueta: "Aplicaciones", valor: String(tabla.filas.length) },
    { etiqueta: "Notas de credito", valor: String(contarNotas(tabla)) },
    {
      etiqueta: "Total IVA",
      valor: (totalIva ? Number(totalIva.valor) : 0).toLocaleString("es-MX", {
        style: "currency",
        currency: "MXN",
      }),
    },
  ];
};

const descargar = ({ tabla, titulo, nombreHojaExcel, prefijo, empresaNombre, periodo, notasOcultas }) => ({
  excel: () =>
    descargarLibro(
      [
        {
          nombre: nombreHojaExcel,
          hoja: construirHojaTabla(tabla, {
            titulo,
            empresaNombre,
            descripcionFiltros: descripcionFiltros(periodo, tabla, notasOcultas),
          }),
        },
      ],
      nombreArchivo(prefijo, empresaNombre, periodo)
    ),
  pdf: () =>
    pdfMake
      .createPdf(
        reporteFiscalPdf({
          titulo,
          empresaNombre,
          descripcionFiltros: descripcionFiltros(periodo, tabla, notasOcultas),
          indicadores: indicadoresDe(tabla),
          contador: tabla.filas.length,
          secciones: [{ titulo, ...paraPdf(tabla) }],
        })
      )
      .download(`${nombreArchivo(prefijo, empresaNombre, periodo)}.pdf`),
});

export const descargarExcelCobros = ({ datos, empresaNombre, periodo, notasOcultas }) =>
  descargar({
    tabla: construirTablaCobros(datos),
    titulo: TITULO_COBROS,
    nombreHojaExcel: "Cobros",
    prefijo: "Papel_IVA_Cobros",
    empresaNombre,
    periodo,
    notasOcultas,
  }).excel();

export const descargarPdfCobros = ({ datos, empresaNombre, periodo, notasOcultas }) =>
  descargar({
    tabla: construirTablaCobros(datos),
    titulo: TITULO_COBROS,
    nombreHojaExcel: "Cobros",
    prefijo: "Papel_IVA_Cobros",
    empresaNombre,
    periodo,
    notasOcultas,
  }).pdf();

export const descargarExcelPagos = ({ datos, empresaNombre, periodo, notasOcultas }) =>
  descargar({
    tabla: construirTablaPagos(datos),
    titulo: TITULO_PAGOS,
    nombreHojaExcel: "Pagos",
    prefijo: "Papel_IVA_Pagos",
    empresaNombre,
    periodo,
    notasOcultas,
  }).excel();

export const descargarPdfPagos = ({ datos, empresaNombre, periodo, notasOcultas }) =>
  descargar({
    tabla: construirTablaPagos(datos),
    titulo: TITULO_PAGOS,
    nombreHojaExcel: "Pagos",
    prefijo: "Papel_IVA_Pagos",
    empresaNombre,
    periodo,
    notasOcultas,
  }).pdf();
